import Image from "next/image";
import { ReactNode } from "react";
import { useMedia } from "react-use";
import { MOBILE_MAX_WIDTH } from "../../../constant";
import { useTranslation } from "../../../services/i18n";
import styles from "./footer.module.scss";

type Props = {
  children?: ReactNode;
  isFooterFixed: boolean;
};

const Footer = ({ isFooterFixed }: Props) => {
  const isMobile = useMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`, false);
  const t = useTranslation();

  return (
    <footer className={styles["footer"]}>
      <div>
        <div className={styles["sub-kv-wrapper"]}>
          <Image
            src="/images/sub_kv.png"
            alt="Nascent Neural Elective N"
            layout="fill"
            objectFit="contain"
          />
        </div>
        {!isMobile && <Copyright />}
      </div>
      <div className="flex flex-col justify-end gap-[23px]">
        <div className={styles["sns-link-items-wrapper"]}>
          <TwitterLink />
          <DiscordLink />
          <InstagramLink />
          <YoutubeLink />
        </div>
        <div className={styles["text-link-items-wrapper"]}>
          <TextLinkWrapper
            href="https://towakiseki.notion.site/Privacy-Policy-8e97f37545b94af197fe7b49df647795"
            text="Privacy Policy"
          />
          <TextLinkWrapper
            href="https://towakiseki.notion.site/Special-Commercial-Code-de6ab10dc09a4e8183c2f33a96cf1967"
            text="Special Commercial Code"
          />
          <TextLinkWrapper
            href="https://towakiseki.notion.site/terms-of-service-76fc04b0ebd442c4a24e52ec9ab14c96"
            text="terms of service"
          />
          <TextLinkWrapper
            href={t("guidelineUrl") as string}
            text="Guideline"
          />
          <TextLinkWrapper href={t("helpLink") as string} text="Help" />
        </div>
      </div>
      {isMobile && <Copyright />}
    </footer>
  );
};

const Copyright = () => (
  <div className={styles.copy}>&copy; Pictoria Inc. 2023</div>
);

const TwitterLink = () => (
  <SnsLinkWrapper href="https://twitter.com/NEN_Studio_2">
    <svg
      width="32"
      height="26"
      viewBox="0 0 32 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M28.6387 6.55132C28.6581 6.83132 28.6581 7.11132 28.6581 7.3939C28.6581 16.0042 22.1032 25.9345 10.1174 25.9345V25.9294C6.57679 25.9345 3.10969 24.9204 0.129044 23.0081C0.643882 23.07 1.1613 23.101 1.68001 23.1023C4.6142 23.1049 7.46453 22.1203 9.77291 20.3074C6.98453 20.2545 4.53937 18.4365 3.68517 15.7823C4.66195 15.9707 5.6684 15.932 6.62711 15.67C3.58711 15.0558 1.40001 12.3849 1.40001 9.28293C1.40001 9.25455 1.40001 9.22745 1.40001 9.20035C2.30582 9.70487 3.32001 9.98487 4.35743 10.0158C1.4942 8.10229 0.611624 4.29325 2.34066 1.31519C5.64904 5.38616 10.5303 7.861 15.7703 8.12293C15.2452 5.85971 15.9626 3.48809 17.6555 1.89713C20.28 -0.569971 24.4078 -0.443519 26.8748 2.17971C28.3342 1.89196 29.7329 1.35648 31.0129 0.597771C30.5265 2.10616 29.5084 3.38745 28.1484 4.20164C29.44 4.04938 30.7019 3.70358 31.8903 3.17584C31.0155 4.4868 29.9136 5.62874 28.6387 6.55132Z"
        fill="white"
      />
    </svg>
  </SnsLinkWrapper>
);

const DiscordLink = () => (
  <SnsLinkWrapper href="https://discord.gg/CnArFbMvTp">
    <svg
      width="32"
      height="25"
      viewBox="0 0 32 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M27.107 2.03114C25.0046 1.0686 22.7851 0.385735 20.5052 0C20.1932 0.557717 19.9109 1.13153 19.6595 1.71905C17.231 1.35309 14.7613 1.35309 12.3328 1.71905C12.0813 1.13159 11.799 0.557784 11.4871 0C9.20578 0.388993 6.9848 1.07348 4.88026 2.03618C0.702192 8.2177 -0.430415 14.2457 0.135889 20.1881C2.58267 21.9959 5.32131 23.3707 8.23277 24.2529C8.88835 23.3712 9.46845 22.4358 9.96692 21.4566C9.02015 21.103 8.10634 20.6667 7.23608 20.1529C7.46512 19.9867 7.68912 19.8156 7.90558 19.6495C10.4378 20.8403 13.2017 21.4578 15.9999 21.4578C18.7982 21.4578 21.5621 20.8403 24.0943 19.6495C24.3133 19.8282 24.5373 19.9993 24.7638 20.1529C23.8919 20.6676 22.9764 21.1047 22.0279 21.4591C22.5258 22.4378 23.1059 23.3725 23.7621 24.2529C26.676 23.3743 29.4168 22.0001 31.864 20.1906C32.5285 13.2993 30.7289 7.32671 27.107 2.03114ZM10.6842 16.5336C9.10614 16.5336 7.80238 15.1014 7.80238 13.3396C7.80238 11.5778 9.06084 10.1331 10.6792 10.1331C12.2976 10.1331 13.5913 11.5778 13.5636 13.3396C13.5359 15.1014 12.2925 16.5336 10.6842 16.5336ZM21.3156 16.5336C19.735 16.5336 18.4363 15.1014 18.4363 13.3396C18.4363 11.5778 19.6948 10.1331 21.3156 10.1331C22.9365 10.1331 24.2202 11.5778 24.1925 13.3396C24.1648 15.1014 22.924 16.5336 21.3156 16.5336Z"
        fill="white"
      />
    </svg>
  </SnsLinkWrapper>
);

const InstagramLink = () => (
  <SnsLinkWrapper href="https://www.instagram.com/n_nenstudio/">
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9.37338 0.112389C7.67098 0.192709 6.50841 0.46439 5.49209 0.863751C4.44024 1.27367 3.54872 1.82375 2.66167 2.714C1.77463 3.60424 1.22839 4.4964 0.821347 5.54985C0.427426 6.56841 0.160545 7.73194 0.0853442 9.4353C0.0101439 11.1387 -0.00649615 11.6862 0.00182388 16.0312C0.0101439 20.3761 0.029344 20.9208 0.111904 22.6277C0.193185 24.3298 0.463906 25.492 0.863267 26.5086C1.27383 27.5605 1.82327 28.4517 2.71384 29.3391C3.6044 30.2264 4.49592 30.7714 5.55193 31.1791C6.56953 31.5723 7.73338 31.8405 9.43642 31.9151C11.1395 31.9896 11.6876 32.0069 16.0313 31.9986C20.375 31.9903 20.9219 31.9711 22.6285 31.8901C24.335 31.8091 25.4912 31.5365 26.5082 31.1391C27.56 30.7275 28.4519 30.1791 29.3386 29.2882C30.2253 28.3973 30.7712 27.5045 31.178 26.4504C31.5722 25.4328 31.84 24.269 31.914 22.5672C31.9885 20.8593 32.0061 20.3141 31.9978 15.9697C31.9895 11.6254 31.97 11.0807 31.889 9.3745C31.808 7.66825 31.537 6.50953 31.138 5.49225C30.7267 4.4404 30.1779 3.54984 29.2877 2.66184C28.3975 1.77383 27.504 1.22823 26.4503 0.822471C25.432 0.42855 24.2688 0.160069 22.5658 0.0864688C20.8627 0.0128685 20.3145 -0.00601156 15.9693 0.00230847C11.624 0.0106285 11.08 0.0291886 9.37338 0.112389ZM9.56026 29.0366C8.00026 28.9688 7.15321 28.7096 6.58873 28.4926C5.84121 28.2046 5.30873 27.8565 4.74616 27.2994C4.1836 26.7422 3.838 26.2078 3.54616 25.4619C3.32696 24.8974 3.06296 24.0514 2.99 22.4913C2.91064 20.8053 2.894 20.299 2.88472 16.0273C2.87544 11.7556 2.89176 11.25 2.96568 9.5633C3.03224 8.00458 3.29304 7.15657 3.50968 6.59241C3.79768 5.84393 4.14456 5.31241 4.70296 4.75016C5.26137 4.18792 5.79417 3.84168 6.54073 3.54984C7.10457 3.32968 7.95066 3.06792 9.51002 2.99368C11.1974 2.91368 11.703 2.89768 15.9741 2.8884C20.2451 2.87912 20.752 2.89512 22.44 2.96936C23.9987 3.0372 24.847 3.29544 25.4106 3.51336C26.1584 3.80136 26.6906 4.14728 27.2528 4.70665C27.8151 5.26601 28.1616 5.79689 28.4535 6.54505C28.6739 7.10729 28.9357 7.95306 29.0093 9.51338C29.0896 11.2007 29.1079 11.7067 29.1155 15.9774C29.1232 20.2481 29.1082 20.7553 29.0343 22.4414C28.9661 24.0014 28.7075 24.8488 28.4903 25.4139C28.2023 26.1611 27.8551 26.6939 27.2963 27.2558C26.7376 27.8178 26.2055 28.164 25.4586 28.4558C24.8954 28.6757 24.0483 28.9381 22.4902 29.0123C20.8029 29.0917 20.2973 29.1083 16.0246 29.1176C11.752 29.1269 11.248 29.1096 9.56058 29.0366M22.6038 7.44905C22.6045 7.82882 22.7177 8.19987 22.9292 8.51528C23.1408 8.83068 23.4411 9.07627 23.7922 9.22099C24.1433 9.36571 24.5295 9.40305 24.9018 9.32829C25.2741 9.25353 25.6159 9.07004 25.884 8.80101C26.152 8.53198 26.3343 8.1895 26.4077 7.81689C26.481 7.44428 26.4423 7.05828 26.2963 6.7077C26.1503 6.35712 25.9036 6.05771 25.5874 5.84734C25.2712 5.63697 24.8998 5.52509 24.52 5.52585C24.0109 5.52687 23.523 5.73004 23.1637 6.09069C22.8044 6.45134 22.603 6.93995 22.6038 7.44905ZM7.78458 16.0164C7.79354 20.5541 11.4787 24.2242 16.0153 24.2155C20.552 24.2069 24.2246 20.5221 24.216 15.9844C24.2074 11.4468 20.5213 7.77577 15.984 7.78474C11.4467 7.79369 7.77594 11.4795 7.78458 16.0164ZM10.6665 16.0107C10.6644 14.9558 10.9752 13.924 11.5595 13.0458C12.1438 12.1676 12.9754 11.4823 13.9492 11.0767C14.9229 10.6711 15.9951 10.5634 17.0301 10.7671C18.0651 10.9709 19.0164 11.4769 19.7638 12.2213C20.5112 12.9658 21.021 13.9151 21.2289 14.9493C21.4367 15.9834 21.3332 17.056 20.9315 18.0314C20.5297 19.0068 19.8478 19.8411 18.9719 20.4289C18.0959 21.0166 17.0654 21.3315 16.0105 21.3336C15.3101 21.3351 14.6163 21.1985 13.9686 20.9318C13.3209 20.6651 12.7322 20.2734 12.2359 19.7791C11.7397 19.2848 11.3456 18.6976 11.0764 18.051C10.8071 17.4044 10.6678 16.7111 10.6665 16.0107Z"
        fill="white"
      />
    </svg>
  </SnsLinkWrapper>
);

const YoutubeLink = () => (
  <SnsLinkWrapper href="https://youtube.com/@HumanoidAI_N">
    <svg
      width="32"
      height="24"
      viewBox="0 0 32 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M31.3309 4.3198C30.9636 2.93435 29.8782 1.84344 28.5018 1.47253C26.0073 0.799805 16 0.799805 16 0.799805C16 0.799805 5.99273 0.799805 3.49818 1.47253C2.12182 1.84344 1.03636 2.93435 0.669091 4.3198C0 6.83253 0 12.0725 0 12.0725C0 12.0725 0 17.3125 0.669091 19.8253C1.03636 21.2107 2.12182 22.3016 3.49818 22.6725C5.99455 23.3453 16 23.3453 16 23.3453C16 23.3453 26.0073 23.3453 28.5018 22.6725C29.8782 22.3016 30.9636 21.2107 31.3309 19.8253C32 17.3143 32 12.0725 32 12.0725C32 12.0725 32 6.83253 31.3309 4.3198ZM12.7273 16.8307V7.31435L21.0909 12.0725L12.7273 16.8307Z"
        fill="white"
      />
    </svg>
  </SnsLinkWrapper>
);

const SnsLinkWrapper = ({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={styles["sns-link-wrapper"]}
  >
    {children}
  </a>
);

const TextLinkWrapper = ({ href, text }: { href: string; text: string }) => (
  <div className={styles["text-link-wrapper"]}>
    <a href={href} target="_blank" rel="noopener noreferrer">
      {text}
    </a>
    <div className={styles["underline"]} />
  </div>
);

export default Footer;
